/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Control, Controller, SubmitHandler, useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  FormCancelButton,
  FormDialogActions,
  FormElement,
  FormInputsStack,
  MultiSelectorAutocomplete,
  RichTextEditor,
  formatCountryCodesToString,
  getFormattedDateShortMonthDDYYYY,
  useApiClient,
  useFetch,
  useFetchImmutable,
  getSubmitButtonProps,
  TemplatePluginHandle,
} from '@marageti/z4-lib';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { TicketType, TicketSubType, TicketStatus } from '@marageti/z4-sdk/lib/tickets';
import { TemplateType } from '@marageti/z4-sdk/lib/templates/Template';
import { TripMatch, TripRequest } from '@marageti/z4-sdk/lib/travel';
import ChooseTemplateDialog from '../choose-template-dialog';
import TravelerLookupInput from '../traveler-lookup-input';
import {
  AssigneeOption,
  TYPE_FILTER_OPTIONS,
  STATUS_MENU_OPTIONS,
  TicketTypeOption,
  TicketStatusOption,
  TripRequestOption,
  TripMatchOption,
  TravelerOption,
  TicketFormData,
  EditableTicket,
} from '../../pages/tickets/options';

const TICKET_TEMPLATE_TYPES = [TemplateType.TicketTemplate];

type TicketEditModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: TicketFormData) => Promise<void>;
  assignees: AssigneeOption[];
  ticket?: EditableTicket;
  defaultValues?: Partial<{
    ticketType: TicketType;
    ticketSubType: TicketSubType;
    status: TicketStatus;
    title: string;
    description: string;
    assigneeIds: string[];
    travelerId: string;
    travelerName: string;
    travelerEmail: string;
    tripRequestId: string;
    tripMatchId: string;
  }>;
  isNew?: boolean;
};

const TICKET_TYPE_OPTIONS: TicketTypeOption[] = TYPE_FILTER_OPTIONS.map((opt) => ({
  label: opt.title,
  value: opt.value as TicketSubType,
  ticketType: 'ISSUE' as TicketType,
}));

const TICKET_STATUS_OPTIONS: TicketStatusOption[] = STATUS_MENU_OPTIONS
  .map((opt) => ({ label: opt.label, value: opt.value }));

const ticketFormSchema = z.object({
  type: z.object({
    label: z.string().min(1),
    value: z.string().min(1),
    ticketType: z.string().min(1),
  }).nullable(),
  status: z.object({
    label: z.string().min(1),
    value: z.string().min(1),
  }),
  title: z.string().min(1, 'Title is required').max(100, 'Title must be 100 characters or less'),
  description: z.string().min(1, 'Description is required'),
  traveler: z.object({
    id: z.string(),
    fullName: z.string(),
    email: z.string(),
  }).nullable().optional(),
  tripRequest: z.object({
    label: z.string(),
    value: z.string(),
  }).nullable().optional(),
  tripMatch: z.object({
    label: z.string(),
    value: z.string(),
  }).nullable().optional(),
  assignees: z.array(z.object({
    title: z.string(),
    value: z.string().min(1),
    status: z.string().optional(),
  })).min(1, 'At least one assignee is required'),
}).superRefine((data, ctx) => {
  if (!data.type) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Type is required',
      path: ['type'],
    });
  }

  if (!data.traveler?.id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Traveler is required',
      path: ['traveler'],
    });
  }
  if (!data.tripRequest?.value) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Trip Request is required',
      path: ['tripRequest'],
    });
  }
});

const getTicketTypeOption = (
  value?: TicketTypeOption['value'],
): TicketTypeOption | null => TICKET_TYPE_OPTIONS.find((option) => option.value === value)
  || null;

const getTicketStatusOption = (
  value?: TicketStatusOption['value'],
): TicketStatusOption => TICKET_STATUS_OPTIONS.find((option) => option.value === value)
  || TICKET_STATUS_OPTIONS[0];

const getModalTitle = (isNew: boolean) => (isNew ? 'New Ticket' : 'Edit Ticket');

const renderTripMatchAutocomplete = (control: Control<TicketFormData>, tripMatchOptions: TripMatchOption[], disabled?: boolean) => (
  <Controller
    control={control}
    name="tripMatch"
    render={({ field }) => (
      <Autocomplete
        {...field}
        fullWidth
        id="ticket-trip-match"
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        options={tripMatchOptions}
        onChange={(_, value) => {
          field.onChange(value ?? null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Trip Match"
            disabled={disabled}
          />
        )}
        value={field.value || null}
      />
    )}
  />
);
const TicketEditModal = ({
  open,
  onClose,
  onSave,
  assignees,
  ticket,
  defaultValues,
  isNew = false,
}: TicketEditModalProps) => {
  const apiClient = useApiClient();
  const isEditing = !isNew && !!ticket;
  const hasTripRequestContext = !isEditing
    && !!defaultValues?.travelerId && !!defaultValues?.tripRequestId;
  const [selectedTraveler, setSelectedTraveler] = useState<Traveler | undefined>(undefined);
  const [showChooseTemplate, setShowChooseTemplate] = useState(false);
  const templatePluginRef = useRef<TemplatePluginHandle>(null);

  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(
    apiClient.standardsClient.getCountryCodes,
    [],
    true,
  );

  const {
    data: travelerTripRequestsData,
    isLoading: isLoadingTripRequests,
  } = useFetch<SearchResult<TripRequest>>(
    apiClient.tripRequestsClient.postTripRequestSearches,
    [{
      filterBy: [{ field: 'travelers.id', value: [selectedTraveler?.id || ''] }],
      sortBy: [{ field: 'createdAt', order: 'desc' }],
      pagination: { limit: 100, page: 1 },
    }],
    !hasTripRequestContext && !!selectedTraveler?.id,
  );

  const tripRequests = travelerTripRequestsData?.hits || [];

  const initialTraveler = useMemo<TravelerOption>(() => {
    if (isEditing) {
      if (!ticket?.travelerId || !ticket?.travelerName) return null;
      return { id: ticket.travelerId, fullName: ticket.travelerName, email: '' };
    }
    const travelerId = defaultValues?.travelerId;
    const travelerName = defaultValues?.travelerName;
    const travelerEmail = defaultValues?.travelerEmail;

    if (!travelerId || !travelerName) return null;
    return { id: travelerId, fullName: travelerName, email: travelerEmail || '' };
  }, [
    isEditing,
    ticket?.travelerId,
    ticket?.travelerName,
    defaultValues?.travelerEmail,
    defaultValues?.travelerId,
    defaultValues?.travelerName,
  ]);

  const tripRequestOptions = useMemo<TripRequestOption[]>(() => {
    if (!tripRequests || !countryCodes) return [];
    return tripRequests.map((tripRequest) => {
      const destinations = formatCountryCodesToString(tripRequest.countries, countryCodes);
      const departureDate = getFormattedDateShortMonthDDYYYY(tripRequest.departureDate);
      return {
        label: `${destinations} - ${departureDate}`,
        value: tripRequest.id,
      };
    });
  }, [tripRequests, countryCodes]);

  const [selectedTripRequestId, setSelectedTripRequestId] = useState<string>(
    defaultValues?.tripRequestId || ticket?.tripRequestId || '',
  );

  const {
    data: tripMatchesData,
    isLoading: isLoadingTripMatches,
  } = useFetch<TripMatch[]>(
    apiClient.tripRequestsClient.getTripRequestTripMatches,
    [selectedTripRequestId || ''],
    !!selectedTripRequestId,
  );

  const tripMatchOptions = useMemo<TripMatchOption[]>(() => {
    if (!tripMatchesData || !countryCodes) return [];
    return [...tripMatchesData]
      .sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime())
      .map((tripMatch) => ({
        label: `${formatCountryCodesToString(tripMatch.countries, countryCodes)} – ${tripMatch.partnerName || 'Unknown Travel Company'}`,
        value: tripMatch.id,
      }));
  }, [tripMatchesData, countryCodes]);

  const initialAssignedTo = useMemo(() => {
    const ticketAssigneeIds = isEditing ? ticket?.assignees?.map((a) => a.id) : undefined;
    const assigneeIds = (ticketAssigneeIds?.length
      ? ticketAssigneeIds
      : defaultValues?.assigneeIds) || [];
    if (!assigneeIds.length) return [];
    return assigneeIds
      .map((id) => assignees.find((option) => option.value === id))
      .filter((option): option is AssigneeOption => !!option);
  }, [isEditing, assignees, defaultValues?.assigneeIds, ticket?.assignees]);

  const initialType = useMemo(
    () => getTicketTypeOption(defaultValues?.ticketSubType || ticket?.ticketSubType),
    [defaultValues?.ticketSubType, ticket?.ticketSubType],
  );

  const initialStatus = useMemo(
    () => getTicketStatusOption(defaultValues?.status || ticket?.status),
    [defaultValues?.status, ticket?.status],
  );

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    getValues,
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<TicketFormData>({
    resolver: zodResolver(ticketFormSchema),
    mode: 'onTouched',
    defaultValues: {
      type: initialType,
      status: initialStatus,
      title: defaultValues?.title || ticket?.title || '',
      description: defaultValues?.description || ticket?.description || '',
      traveler: initialTraveler,
      tripRequest: hasTripRequestContext
        ? { label: '', value: defaultValues?.tripRequestId || '' }
        : undefined,
      tripMatch: null,
      assignees: initialAssignedTo,
    },
  });

  const watcherTripRequest = watch('tripRequest');
  const watcherTitle = watch('title');

  const formScopeKey = useMemo(
    () => (isNew
      ? `new:${defaultValues?.tripRequestId ?? ''}:${defaultValues?.travelerId ?? ''}`
      : `edit:${ticket?.id ?? ''}`),
    [isNew, ticket?.id, defaultValues?.tripRequestId, defaultValues?.travelerId],
  );

  // Reset form state when the modal opens or its scope changes.
  useEffect(() => {
    if (!open) return;
    reset({
      type: initialType,
      status: initialStatus,
      title: defaultValues?.title || ticket?.title || '',
      description: defaultValues?.description || ticket?.description || '',
      traveler: initialTraveler,
      tripRequest: hasTripRequestContext
        ? { label: '', value: defaultValues?.tripRequestId || '' }
        : undefined,
      tripMatch: null,
      assignees: initialAssignedTo,
    });
    setSelectedTraveler(initialTraveler ? ({
      id: initialTraveler.id,
      fullName: initialTraveler.fullName,
      email: initialTraveler.email,
    } as Traveler) : undefined);
    setSelectedTripRequestId(
      defaultValues?.tripRequestId || ticket?.tripRequestId || '',
    );
  }, [open, formScopeKey]);

  // Patch assignees once team-member options finish loading (they may arrive after reset).
  useEffect(() => {
    if (!open || !initialAssignedTo.length) return;
    const current = getValues('assignees');
    if (current.length > 0) return;
    setValue('assignees', initialAssignedTo, { shouldValidate: true });
  }, [open, initialAssignedTo, getValues, setValue]);

  const initialTripMatchId = defaultValues?.tripMatchId || ticket?.tripMatchId || '';

  // Hydrate the Trip Request field from async options.
  // In edit mode: find and set the matching option once options load.
  // In create mode with manual traveler: auto-select the first available TR.
  useEffect(() => {
    if (!open) return;

    if (isEditing) {
      if (!selectedTripRequestId) return;
      const currentTR = getValues('tripRequest');
      const trOption = tripRequestOptions.find((o) => o.value === selectedTripRequestId);
      if (trOption) {
        if (!currentTR?.value || currentTR.value !== selectedTripRequestId) {
          setValue('tripRequest', trOption, { shouldValidate: true });
        }
      } else if (currentTR?.value && currentTR.value !== selectedTripRequestId) {
        setValue('tripRequest', undefined, { shouldValidate: true });
      }
      return;
    }

    if (hasTripRequestContext || !selectedTraveler) return;
    if (!tripRequestOptions.length) {
      setSelectedTripRequestId('');
      setValue('tripRequest', undefined);
      setValue('tripMatch', null);
      return;
    }
    const currentTR = getValues('tripRequest');
    if (!currentTR?.value || !tripRequestOptions.some((o) => o.value === currentTR.value)) {
      const firstOption = tripRequestOptions[0];
      setValue('tripRequest', firstOption, { shouldValidate: true });
      setSelectedTripRequestId(firstOption.value);
      setValue('tripMatch', null);
    }
  }, [open, isEditing, hasTripRequestContext, selectedTraveler, tripRequestOptions, selectedTripRequestId, getValues, setValue]);

  // Hydrate the Trip Match field once trip match options load.
  useEffect(() => {
    if (!open) return;
    const currentTM = getValues('tripMatch');
    if (!initialTripMatchId) {
      if (currentTM?.value && tripMatchOptions.length > 0
        && !tripMatchOptions.some((o) => o.value === currentTM.value)) {
        setValue('tripMatch', null);
      }
      return;
    }
    const tmOption = tripMatchOptions.find((o) => o.value === initialTripMatchId);
    if (tmOption) {
      if (!currentTM?.value || currentTM.value !== initialTripMatchId) {
        setValue('tripMatch', tmOption);
      }
    } else if (currentTM?.value && currentTM.value !== initialTripMatchId) {
      setValue('tripMatch', null);
    }
  }, [open, tripMatchOptions, initialTripMatchId, getValues, setValue]);

  const editorKey = `${ticket?.id || 'new'}-${open}`;

  const onSubmit: SubmitHandler<TicketFormData> = async (data) => {
    await onSave(data);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>{getModalTitle(isNew)}</DialogTitle>
      <FormElement
        name="ticket-edit-form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <DialogContent
          sx={{
            paddingTop: 0.5,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pb: 4,
          }}
        >
          <FormInputsStack>
            <Box display="flex" gap={2}>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    id="ticket-type"
                    getOptionLabel={(option) => option.label}
                    getOptionKey={(option) => option.value}
                    disableClearable
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    options={TICKET_TYPE_OPTIONS}
                    onChange={(_, value) => field.onChange(value || null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Type"
                        required
                        error={!!errors.type}
                        helperText={(errors.type as { message?: string } | undefined)?.message}
                      />
                    )}
                    value={field.value || undefined}
                    sx={{ flex: 1 }}
                  />
                )}
              />
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    id="ticket-status"
                    getOptionLabel={(option) => option.label}
                    getOptionKey={(option) => option.value}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    disableClearable
                    getOptionDisabled={(option) => option.value === 'COMPLETED'}
                    options={TICKET_STATUS_OPTIONS}
                    onChange={(_, value) => field.onChange(value || TICKET_STATUS_OPTIONS[0])}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        required
                        error={!!errors.status}
                        helperText={errors.status?.value?.message || errors.status?.message}
                      />
                    )}
                    value={field.value}
                    sx={{ flex: 1 }}
                  />
                )}
              />
            </Box>

            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  required
                  fullWidth
                  inputProps={{ maxLength: 100 }}
                  error={!!errors.title}
                  FormHelperTextProps={{ sx: { mr: 0, textAlign: 'right' } }}
                  helperText={errors.title?.message || `${100 - (watcherTitle?.length || 0)} characters available`}
                />
              )}
            />

            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <RichTextEditor
                  key={editorKey}
                  editorName="ticket-description-editor"
                  defaultValue={field.value || ''}
                  label="Description *"
                  onChange={field.onChange}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  insertTemplateProps={{
                    onShowTemplateModal: () => setShowChooseTemplate(true),
                    ref: templatePluginRef,
                  }}
                  useLinks
                  useLists
                  sx={{
                    mt: 2.5,
                    minHeight: '207px',
                    maxHeight: '260px',
                    flex: 1,
                  }}
                />
              )}
            />

            {!hasTripRequestContext && (
              <>
                <TravelerLookupInput
                  value={selectedTraveler}
                  onChange={(_: any, value: Traveler | null) => {
                    setSelectedTraveler(value || undefined);
                    setValue('traveler', value ? {
                      id: value.id,
                      fullName: value.fullName,
                      email: value.email,
                    } : null);
                    setValue('tripRequest', undefined);
                    setValue('tripMatch', null);
                    setSelectedTripRequestId('');
                  }}
                  inputProps={{
                    required: true,
                    error: !!errors.traveler,
                    helperText: (errors.traveler as { id?: { message?: string } } | undefined)?.id?.message
                      || (errors.traveler as { message?: string } | undefined)?.message,
                  }}
                />

                <Box display="flex" gap={2} alignItems="flex-start">
                  <Controller
                    control={control}
                    name="tripRequest"
                    render={({ field }) => (isLoadingTripRequests ? (
                      <Skeleton variant="rounded" height={43} width="100%" sx={{ mb: 1.25, mt: 2.5 }} />
                    ) : (
                      <Autocomplete
                        {...field}
                        key={selectedTraveler?.id || 'no-traveler'}
                        fullWidth
                        id="ticket-trip-request"
                        getOptionLabel={(option) => option.label}
                        getOptionKey={(option) => option.value}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        options={tripRequestOptions}
                        onChange={(_, value) => {
                          setSelectedTripRequestId(value?.value || '');
                          field.onChange(value);
                          setValue('tripMatch', null);
                        }}
                        clearIcon={null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Trip Request"
                            required
                            disabled={!selectedTraveler || isLoadingTripRequests}
                            error={!!errors.tripRequest}
                            helperText={
                              errors.tripRequest?.value?.message
                              || (errors.tripRequest as { message?: string } | undefined)?.message
                            }
                          />
                        )}
                        value={field.value || null}
                      />
                    ))}
                  />

                  {isLoadingTripMatches || isLoadingTripRequests ? (
                    <Skeleton variant="rounded" height={43} width="100%" sx={{ mb: 1.25, mt: 2.5 }} />
                  ) : renderTripMatchAutocomplete(control, tripMatchOptions, !selectedTraveler || !watcherTripRequest?.value)}
                </Box>
              </>
            )}

            {!isEditing && hasTripRequestContext && isLoadingTripMatches && (
              <Skeleton variant="rounded" height={43} width="100%" />
            )}

            {!isEditing && hasTripRequestContext && !isLoadingTripMatches
              && tripMatchOptions.length > 0 && renderTripMatchAutocomplete(control, tripMatchOptions)}

            <Controller
              control={control}
              name="assignees"
              render={({ field }) => (
                <MultiSelectorAutocomplete
                  {...field}
                  label="Assignee*"
                  name="ticket-assignees"
                  optionEntities={assignees.filter((option) => option.value !== '')}
                  valueFieldName="value"
                  labelFieldName="title"
                  onChange={(_: any, data: any[]) => {
                    field.onChange(data);
                  }}
                  error={!!errors.assignees}
                  helperText={(errors.assignees as { message?: string } | undefined)?.message}
                />
              )}
            />
          </FormInputsStack>
        </DialogContent>

        <FormDialogActions
          sx={{
            mt: 0,
            border: 0,
            flexShrink: 0,
            borderTop: 1,
            borderTopColor: 'divider',
            py: 2.5,
            px: 3,
          }}
        >
          <FormCancelButton onClick={onClose} />
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, isValid && !isLoadingTripRequests && !isLoadingTripMatches)}
          >
            {isNew ? 'Create' : 'Save'}
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
      <ChooseTemplateDialog
        closeDialog={() => setShowChooseTemplate(false)}
        onSubmit={(text) => templatePluginRef.current?.insertTemplate(text)}
        open={showChooseTemplate}
        templateTypes={TICKET_TEMPLATE_TYPES}
      />
    </Dialog>
  );
};

export default TicketEditModal;
